import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Compass, FileSearch, Sparkles } from "lucide-react";
import { Footer } from "@/components/Footer";
import { FitForm } from "@/components/campaign/FitForm";
import { FitResult } from "@/components/campaign/FitResult";
import { savedFit } from "@/lib/fitScan";
import { BrandLogo } from "@/components/BrandLogo";

const STEPS = [
  [FileSearch, "Upload your resume", "PDF, DOCX or pasted text."],
  [Compass, "Tell us your target role", "We check fit for the job you actually want."],
  [Sparkles, "Get your evaluation", "Resume score, job fit, strengths, gaps and next steps."],
];

export default function FreeResumeCheck() {
  const [params] = useSearchParams();
  const [result, setResult] = useState(savedFit);
  useEffect(() => { const ref = params.get("ref"); if (ref) localStorage.setItem("rpopro_ref", ref); }, [params]);

  const signup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/search";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grain relative min-h-screen overflow-hidden bg-[#09090B]" data-testid="free-resume-check">
      <div className="lp-glow pointer-events-none absolute -right-40 -top-40 h-[620px] w-[620px] rounded-full bg-[#D4AF37]/10 blur-[140px]" />
      <div className="relative z-[2] mx-auto grid max-w-6xl items-start gap-12 px-6 py-12 lg:grid-cols-[1fr_1.1fr] lg:py-20">
        <div className="lg:sticky lg:top-16">
          <Link to="/login"><BrandLogo size="lg" className="fade-up mb-10" testid="frc-logo" /></Link>
          <p className="fade-up mb-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]" style={{ animationDelay: "80ms" }}>Free resume evaluation</p>
          <h1 className="fade-up font-display text-4xl font-semibold leading-[1.08] tracking-tight text-zinc-50 sm:text-5xl lg:text-6xl" style={{ animationDelay: "160ms" }} data-testid="frc-headline">
            Upload Your Resume.<br /><span className="gold-text">Discover Your Career Potential.</span>
          </h1>
          <p className="fade-up mt-6 max-w-lg text-base leading-relaxed text-zinc-400" style={{ animationDelay: "240ms" }}>
            Find out how ATS filters and AI screeners rate your resume, and how well you fit the role you want, in under a minute. Free, no sign-up needed.
          </p>
          <ol className="mt-10 space-y-5">
            {STEPS.map(([Icon, t, d], i) => (
              <li key={t} className="fade-up flex gap-4" style={{ animationDelay: `${320 + i * 90}ms` }}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#D4AF37]/40 bg-[#33290D] text-[#F3C649]"><Icon className="h-5 w-5" /></span>
                <span><span className="block font-display text-zinc-100">{t}</span><span className="text-sm text-zinc-500">{d}</span></span>
              </li>
            ))}
          </ol>
        </div>
        <div className="fade-up rounded-3xl border border-[#D4AF37]/30 bg-[#121214]/90 p-6 shadow-[0_10px_50px_rgba(212,175,55,0.1)] backdrop-blur-xl sm:p-8" style={{ animationDelay: "200ms" }}>
          {result?.checks || result?.score != null ? <FitResult r={result} onCta={signup} /> : <FitForm onDone={setResult} />}
        </div>
      </div>
      <Footer />
    </div>
  );
}
